// 首页（home tab）业务域接口，写法见 README.md
import type { HouseItem } from './common'
import { HouseType } from '@/dict/house-type'
import { http, httpGet, httpPost } from '@/http'

/** 首页 Banner（swagger Banner，只声明展示/跳转用到的字段） */
export interface BannerItem {
  id: number
  /** 封面图 */
  coverImg?: string
  /** 跳转链接 */
  link?: string
  /** 链接类型：1-h5 2-公众号 3-小程序内部页 */
  linkType?: number
  /** 展示位置 */
  location?: number
  /** 排序（越小越靠前） */
  sort?: number
  /** 状态：1-上架 0-下架 */
  status?: number
  title?: string
}

/** 开通城市条目（GET /mini/getCityChoose 返回 OpenCityInfoVo，只声明用到的字段） */
export interface OpenCityItem {
  /** 市名称 */
  cityName: string
  /** 市编码（后端字段名是 code，写入 CityInfo 时才映射为 cityCode，v1 易混点；swagger 声明 integer 但实际返回字符串，以实测为准） */
  code: string
}

/** 当前浏览城市（user store cityInfo / localCityInfo 结构；cityCode 即 OpenCityItem.code） */
export interface CityInfo {
  cityCode: string
  cityName: string
}

/** 获取登录用户下的所有城市（v1 接口 /mini/getCityChoose，城市选择弹层用） */
export const getCityChooseApi = () => httpGet<OpenCityItem[]>('/hxyj/mini/getCityChoose')

/** 获取首页轮播 Banner（POST /mini/banner） */
export function getHomeBannerApi() {
  return httpPost<BannerItem[]>('/hxyj/mini/banner')
}

/** 选城市接口返回（CityCodeVo，selectHousingMiniCity 返回，只声明用到的字段；城市展示不消费此结构，统一走 getLoginUserInfo 的 userInfo.cityCode/cityName） */
export interface LoginCityInfo {
  /** 城市 Code */
  code: number | string
  /** 城市名称 */
  name: string
}

/** 选择房产小程序城市（服务端记忆当前城市，cityCode 走 query 传参；选完后统一走 getLoginUserInfo 刷新，不消费此返回值） */
export function selectHousingMiniCityApi(cityCode: string) {
  return httpPost<LoginCityInfo>('/hxyj/mini/selectHousingMiniCity', undefined, { cityCode })
}

/** 区域下商圈 */
export interface HouseShopItem {
  shopId: string
  shopName: string
}

/** 城市开放区域 */
export interface UrbanAreaItem {
  urbanAreaId: string
  urbanAreaName: string
  houseShopList?: HouseShopItem[]
}

/** 根据开放城市 code 查询全部区域及商圈 */
export function getCityWithinAllRangesApi(code: string) {
  return httpGet<UrbanAreaItem[]>('/hxyj/mini/getCityWithinAllRanges', { code })
}

/** mybatis-plus 分页容器（只声明用到的字段；total 实测返回字符串，消费方需 Number() 兜底） */
export interface PageResult<T> {
  records: T[]
  total: number | string
}

/** 房源列表入参（POST /mini/queryHousingList，本期只用到这些字段，筛选参数重建筛选组时再扩展） */
export interface HousingListParams {
  /** 搜索关键词 */
  keyWord?: string
  /** 页码（从 1 开始） */
  pageNum: number
  /** 每页条数 */
  size: number
  /** 主类型：1-二手房 2-租房（本期固定传 1） */
  type: 1 | 2
  /** 城市编码（由调用方显式传入） */
  cityCode?: string
  subHouseUrbanAreaType?: string[]
  subHouseDefaultSort?: string | number
  subHouseHouseType?: string[]
  priceRangeStart?: number | null
  priceRangeEnd?: number | null
  priceSq?: number | null
  houseKind?: string
  useType?: string
  areaSq?: string
  orientation?: string
  renovation?: string
  /** 精装好房：1-是 0-否 */
  decoratedGoodHouse?: number
  /** 天天优选：1-是 0-否 */
  dayRecommend?: number
  /** 七天上新：1-是 0-否 */
  sevenDaysUpNew?: number
  /** 周度推荐：1-是 0-否 */
  weekRecommend?: number
  /** 小区 id（小区详情页查本小区房源；雪花长整型，字符串原样透传勿 Number 转换） */
  estateId?: number | string
  /** 是否单边房源（小区详情「单边在售」传 true） */
  isSingleSide?: boolean
}

/** 小区列表条目（POST /mini/jointSale/queryEstateList） */
export interface EstateItem {
  /** 小区 id */
  communityId: number
  /** 小区封面 */
  communityImg?: string
  /** 小区名称 */
  communityName: string
  /** 均价（纯数值，展示时拼「元/㎡」） */
  communityPrice?: string
  /** 在售文案（如「在售16套」） */
  communitySale?: string
  /** 在租文案（如「在租12套」） */
  communityRent?: string
}

/** 小区列表入参（POST /mini/jointSale/queryEstateList） */
export interface EstateListParams {
  keyWord?: string
  pageNum: number
  size: number
  cityCode?: string
  subHouseUrbanAreaType?: string[]
}

/** 获取小区列表（POST /mini/jointSale/queryEstateList） */
export function queryEstateListApi(data: EstateListParams) {
  return httpPost<PageResult<EstateItem>>('/hxyj/mini/jointSale/queryEstateList', data)
}

/** 获取房源列表（POST /mini/jointSale/queryHousingList，返回 Page«HousingRegulateListVo»） */
export function queryHousingListApi(data: HousingListParams) {
  return httpPost<PageResult<HouseItem>>('/hxyj/mini/jointSale/queryHousingList', data)
}

/** 精装好房列表（POST /mini/jointSale/queryHousingList，decoratedGoodHouse=1） */
export function getDecoratedGoodHouseListApi(cityCode?: string, size = 3) {
  return queryHousingListApi({
    pageNum: 1,
    size,
    type: HouseType.Sell,
    decoratedGoodHouse: 1,
    cityCode,
  })
}

// ==================== 房源详情 ====================

/**
 * 详情键值行（后端拼好中文文案，前端按 title 查找展示，对齐 v1）：
 * - firstBlock 的 title：point（经纬度 "经度,纬度"）/ 小区 / 地址 / 地铁 / 楼层 / 朝向 / 电梯 / 装修 / 学位
 * - housingIntroductionVos 的 title：售价 | 租价 / 房型 / 建筑面积 / 单价
 */
export interface TitleValueItem {
  title: string
  value: string
}

/** 装修标准条目（HousingDecorationBrandVo） */
export interface DecorationBrandItem {
  id: number
  /** 装修品类（水电料/防水/电线…） */
  categoryName: string
  /** 品牌（保利管道/立邦…） */
  brandName: string
  /** 排序号 */
  sortNo: number
}

/** 小区模块（HousingParticularCommunityVo，只声明用到的字段） */
export interface HousingCommunityInfo {
  communityId?: number
  communityName?: string
  communityImg?: string
  /** 小区均价文案（如 10783元/平） */
  communityPrice?: string
  /** 在租文案（如 在租 21 套） */
  communityRent?: string
  /** 在售文案（如 在售 67 套） */
  communitySale?: string
  /** 小区地址 */
  address?: string
}

/** 经纪人店铺信息（TrusteeshipVo，只声明用到的字段；后端另有 agentId/agentContact/wxQrCode 等，业务需要时再补） */
export interface TrusteeshipInfo {
  /** 是否已关注房源（0/1，v1 实际消费此字段，与根级 isFollowUp 互为兜底） */
  isFollowUp?: number
}

/** 房源详情（GET /mini/queryHousingParticularsById 返回 HousingParticularsVo，只声明用到的字段） */
export interface HousingDetail {
  housingId: number
  /** 房源标题 */
  housingTitle: string
  /** 房源编号 */
  identifier: string
  /** 标签（满五/地铁/全景看房…） */
  labels?: string[]
  /** 房源图片 */
  imgUrl?: string[]
  /** 户型图图片 */
  layoutUrl?: string[]
  /** 房源视频 */
  videoUrl?: string
  /** 分享封面图 */
  coverImg?: string
  /** 分享标题 */
  shareTitle?: string
  /** 是否已被关注 */
  isFollowUp?: boolean
  /** 房源状态（1:出售 2:出租 3:租售 4:暂缓…）：仅 1/2/3 可展示详情，其余（含缺失）视为已下架 */
  status?: number
  /** 价格/户型/面积块（售价|租价 / 房型 / 建筑面积 / 单价） */
  housingIntroductionVos?: TitleValueItem[]
  /** 基础信息块（point/小区/地址/楼层/朝向/电梯/装修/学位…） */
  firstBlock?: TitleValueItem[]
  /** 用途（取 [0].value） */
  purposeInfoVos?: { title?: string, value: string }[]
  /** 装修标准列表（按 sortNo 排序展示，空则隐藏装修标准卡） */
  decorationBrandList?: DecorationBrandItem[]
  /** 小区模块 */
  housingParticularCommunityVo?: HousingCommunityInfo
  /** 经纪人店铺信息 */
  trusteeshipVo?: TrusteeshipInfo
}

/**
 * 查询房源详情（v1 接口 /mini/queryHousingParticularsById）
 * @param id 房源 id
 * @param tab 房源类型：1-二手房 2-租房
 * @param unionId 对齐 v1 传当前用户 openId（user store），取不到传空串
 */
export function queryHousingParticularsByIdApi(id: string | number, tab: 1 | 2, unionId: string) {
  return httpGet<HousingDetail>('/hxyj/mini/queryHousingParticularsById', { id, tab, unionId })
}

/**
 * 获取房源卖点（GET /hxyj/mini/getMiniHousingSellingPoints）
 * @param housingId 房源 ID
 */
export interface HousingSellingPoints {
  sellingPoints?: string
}

// 卖点为营销增强信息，调用方（详情页/海报）失败均静默兜底，不弹错误提示
export function getMiniHousingSellingPointsApi(housingId: string | number) {
  return http<HousingSellingPoints>({ url: '/hxyj/mini/getMiniHousingSellingPoints', method: 'GET', query: { housingId }, hideErrorToast: true })
}

/** 客户点开房源保存浏览记录（埋点静默上报，失败不提示） */
export function saveHousingBrowseRecordApi(data: { housingId: string | number, rentOrSell: 1 | 2 }) {
  return http<object>({ url: '/hxyj/mini/housing-browse-record/insert', method: 'POST', data, hideErrorToast: true })
}

// ==================== 小区详情 ====================

/** 小区基本信息键值行（basicInformation，swagger 声明泛型 object；v1 按固定顺序取 value，name 不保证有值） */
export interface EstateBasicInfoItem {
  name?: string
  value?: string
}

/** 小区详情（GET /mini/queryEstateDetailById 返回 HousingRealEstateParticularsVo，只声明用到的字段） */
export interface EstateDetail {
  /** 小区 id */
  realEstateId: number
  /** 小区名称 */
  title?: string
  /** 小区地址 */
  address?: string
  /** 小区坐标（"经度,纬度"，周边唤起系统地图用） */
  communityPosition?: string
  /** 小区图片 */
  image?: string[]
  /** 均价（元/平；格式未实测，展示侧做剥离单位兜底） */
  price?: string
  /** 在售套数 */
  sales?: string
  /** 在租套数 */
  rent?: string
  /** 基本信息（v1 固定顺序：竣工时间/物业类型/所属商圈/开发商/物业公司） */
  basicInformation?: EstateBasicInfoItem[]
}

/** 查询小区详情（GET /mini/queryEstateDetailById，v1 接口同款） */
export function queryEstateDetailByIdApi(id: string | number) {
  return httpGet<EstateDetail>('/hxyj/mini/queryEstateDetailById', { id })
}

/** 关注小区（POST /mini/houseEstateFollow/addFollow；关注列表接口在 my 域 getFollowCommunityListApi） */
export function followEstateApi(houseRealEstateId: number | string) {
  return httpPost<null>('/hxyj/mini/houseEstateFollow/addFollow', { houseRealEstateId })
}

/** 取消关注小区（POST /mini/houseEstateFollow/unfollow） */
export function unfollowEstateApi(houseRealEstateId: number | string) {
  return httpPost<null>('/hxyj/mini/houseEstateFollow/unfollow', { houseRealEstateId })
}
